import { useState, useEffect, useRef, useCallback } from 'react'
import { PhoneFrameLoadingState } from './PhoneFrameLoadingState'
import { PhoneFrameErrorState } from './PhoneFrameErrorState'

// iPhone 14 Pro logical dimensions
const PHONE_W = 390
const PHONE_H = 844
// Bezel adds border (8px each side) + dynamic island area (25px) + padding
const BEZEL_W = PHONE_W + 32 // 422
const BEZEL_H = PHONE_H + 25 + 32 // 901

type FrameState = 'loading' | 'loaded' | 'error' | 'no-store'

interface PhoneFrameProps {
  src: string | null
  tokenLoading?: boolean
  tokenError?: boolean
  onTokenRetry?: () => void
}

export function PhoneFrame({ src, tokenLoading, tokenError, onTokenRetry }: PhoneFrameProps) {
  const [state, setState] = useState<FrameState>(src ? 'loading' : 'no-store')
  const [retryKey, setRetryKey] = useState(0)
  const [scale, setScale] = useState(0.5)
  const containerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stateRef = useRef<FrameState>(state)

  // Keep stateRef in sync (avoids adding `state` to useEffect deps)
  stateRef.current = state

  // Reset state when src changes — but never override 'loaded' state on token errors
  useEffect(() => {
    const current = stateRef.current
    if (tokenLoading && current !== 'loaded') {
      setState('loading')
    } else if (tokenError && current !== 'loaded') {
      setState('error')
    } else if (!tokenLoading && !tokenError && src) {
      if (current !== 'loaded') setState('loading')
    } else if (!src && !tokenLoading) {
      setState('no-store')
    }
  }, [src, tokenLoading, tokenError])

  // Timeout for loading — 15s max
  useEffect(() => {
    if (state !== 'loading') return
    timeoutRef.current = setTimeout(() => setState('error'), 15_000)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [state, retryKey])

  // ResizeObserver for responsive scaling
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        const { width, height } = entries[0].contentRect
        const s = Math.min(1, (width - 32) / BEZEL_W, (height - 32) / BEZEL_H)
        setScale(Math.max(0.3, s))
      })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setState('loaded')
  }, [])

  const handleRetry = useCallback(() => {
    if (tokenError && onTokenRetry) {
      onTokenRetry()
    } else {
      setRetryKey((k) => k + 1)
      setState('loading')
    }
  }, [tokenError, onTokenRetry])

  const showOverlay = state === 'loading' || state === 'error' || state === 'no-store'

  return (
    <div ref={containerRef} className="flex h-full w-full items-center justify-center p-4">
      {/* Desktop: phone bezel visible (md+), Mobile: hidden */}
      <div className="hidden md:block">
        <div
          style={{
            width: BEZEL_W * scale,
            height: BEZEL_H * scale,
          }}
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              width: BEZEL_W,
              height: BEZEL_H,
            }}
          >
            {/* Bezel */}
            <div className="relative flex flex-col items-center rounded-[3rem] border-[8px] border-gray-900 bg-gray-900 shadow-xl">
              {/* Dynamic Island */}
              <div className="mt-1 h-[25px] w-[100px] rounded-full bg-black" />
              {/* Screen — overscroll-behavior prevents scroll chaining to admin page */}
              <div
                className="relative overflow-hidden rounded-[2.2rem] bg-white"
                style={{ width: PHONE_W, height: PHONE_H, overscrollBehavior: 'contain' }}
              >
                {src && (
                  <iframe
                    key={retryKey}
                    src={src}
                    title="ตัวอย่างร้านค้าบนมือถือ"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    loading="eager"
                    onLoad={handleLoad}
                    className="h-full w-full border-0"
                    style={{ pointerEvents: 'auto' }}
                  />
                )}
                {showOverlay && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    {state === 'loading' && <PhoneFrameLoadingState />}
                    {state === 'error' && <PhoneFrameErrorState onRetry={handleRetry} />}
                    {state === 'no-store' && <PhoneFrameErrorState isNoStore />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: no bezel, full-width iframe */}
      <div className="flex h-full w-full flex-col md:hidden">
        <div
          className="relative flex-1 overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
          style={{ overscrollBehavior: 'contain' }}
        >
          {src && (
            <iframe
              key={`m-${retryKey}`}
              src={src}
              title="ตัวอย่างร้านค้าบนมือถือ"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              loading="eager"
              onLoad={handleLoad}
              className="h-full w-full border-0"
              style={{ pointerEvents: 'auto' }}
            />
          )}
          {showOverlay && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              {state === 'loading' && <PhoneFrameLoadingState />}
              {state === 'error' && <PhoneFrameErrorState onRetry={handleRetry} />}
              {state === 'no-store' && <PhoneFrameErrorState isNoStore />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
